import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  ArrowClockwiseIcon,
  ArrowCounterClockwiseIcon,
  MagicWandIcon,
  PencilSimpleLineIcon,
  PlusIcon,
  TrashSimpleIcon,
} from "@phosphor-icons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useBlocker, useParams } from "@tanstack/react-router";
import isDeepEqual from "fast-deep-equal";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import type { DialogProps } from "@/dialogs/store";
import type { AiEditResult } from "@/integrations/multiresume/ai/section-editing";
import type {
  AiEditDraft,
  AiEditRoleDraft,
  AiEditUiTargetSnapshot,
  AiEditUrlDraft,
} from "@/integrations/multiresume/ai/section-editing-ui";
import type { ResumeData } from "@/schema/resume/data";

import { RichInput, TiptapContent } from "@/components/input/rich-input";
import { URLInput } from "@/components/input/url-input";
import { flushResumeSync, useResumeStore } from "@/components/resume/store/resume";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useConfirm } from "@/hooks/use-confirm";
import { useDialogCloseGuard } from "@/hooks/use-dialog-close-guard";
import { useQuotaPreflight } from "@/hooks/use-quota-preflight";
import {
  applyAiEditDraftToResumeData,
  createAiEditTuneDraft,
  emptyAiEditDraft,
  getAiEditDraftMainHtml,
  getAiEditTargetKey,
  getAiEditUiTargetSnapshot,
  hasAiEditDraftAiEligibleContent,
  hasAiEditDraftRequiredContent,
  mergeAiEditResultIntoDraft,
  setAiEditDraftMainHtml,
  toAiEditResult,
} from "@/integrations/multiresume/ai/section-editing-ui";
import { getAiActionCost } from "@/integrations/multiresume/quota/costs";
import { client, orpc, type RouterInput } from "@/integrations/orpc/client";
import { stripHtml } from "@/utils/string";
import { generateId } from "@/utils/string";
import { cn } from "@/utils/style";
import { getUserFacingError } from "@/utils/user-facing-error";

import { useDialogStore } from "../store";
import { getAiEditQuotaAction } from "./ai-edit-quota";
import { resolveOptimizationPlanMutation } from "./ai-edit-save";

type Props = DialogProps<"resume.ai-edit">;

type AiPanelMode = "default" | "generated" | "tune";

type DraftHistory = {
  past: AiEditDraft[];
  present: AiEditDraft;
  future: AiEditDraft[];
};

type AiEditResponseState = {
  suggestions: string[];
  reasons: string[];
};

type OptimizeMutationInput = {
  operationId: string;
  requestedOperation: "generate" | "regenerate";
  requestDraft: AiEditDraft;
  originalDraft: AiEditDraft;
  customPrompt: string;
};

function requiresNewRegenerationOperation(error: unknown) {
  if (!error || typeof error !== "object") return false;
  const data = Reflect.get(error, "data");
  return !!data && typeof data === "object" && Reflect.get(data, "regenerationRetry") === "new_operation_required";
}

const maxDraftHistory = 50;

const emptyAiEditResponse: AiEditResponseState = {
  suggestions: [],
  reasons: [],
};

function createAiEditResponseFromRecommendation(recommendation?: Props["data"]["recommendation"]): AiEditResponseState {
  if (!recommendation || (recommendation.suggestions.length === 0 && (recommendation.reasons ?? []).length === 0)) {
    return emptyAiEditResponse;
  }

  return {
    suggestions: recommendation.suggestions,
    reasons: recommendation.reasons ?? [],
  };
}

function isHandledRecommendation(recommendation?: Props["data"]["recommendation"]) {
  return (
    recommendation?.status === "applied" || recommendation?.status === "ignored" || recommendation?.status === "edited"
  );
}

function isHideInTargetRecommendation(recommendation?: Props["data"]["recommendation"]) {
  return recommendation?.proposalType === "hide_in_target" && !isHandledRecommendation(recommendation);
}

function hasReviewableRecommendation(recommendation?: Props["data"]["recommendation"]) {
  return (
    !isHandledRecommendation(recommendation) &&
    (!!recommendation?.draft || isHideInTargetRecommendation(recommendation))
  );
}

function hasPreloadedDraft(recommendation?: Props["data"]["recommendation"]) {
  return !!recommendation?.draft && !isHandledRecommendation(recommendation);
}

function shouldPreloadDraftOnMount(target: Props["data"]["target"], recommendation?: Props["data"]["recommendation"]) {
  // Summary: preload draft if present and unhandled.
  if (target.type === "summary") return hasPreloadedDraft(recommendation);
  // Experience / Project with update draft: open directly in AI review per Phase 5.
  // hide_in_target proposals keep the DefaultEditPane UI with confirm/ignore buttons.
  if (isHideInTargetRecommendation(recommendation)) return false;
  return hasPreloadedDraft(recommendation);
}

function createDraftFromRecommendation(original: AiEditDraft, recommendation?: Props["data"]["recommendation"]) {
  if (isHideInTargetRecommendation(recommendation) && original.type !== "summary") {
    return { ...original, hidden: true };
  }

  if (hasPreloadedDraft(recommendation) && recommendation?.draft) {
    return mergeAiEditResultIntoDraft(original, recommendation.draft);
  }

  return original;
}

function createInitialDraft(
  original: AiEditDraft,
  target: Props["data"]["target"],
  recommendation?: Props["data"]["recommendation"],
) {
  return shouldPreloadDraftOnMount(target, recommendation)
    ? createDraftFromRecommendation(original, recommendation)
    : original;
}

function hasTextContent(value: string) {
  return stripHtml(value).length > 0;
}

function recommendationEvidenceSourceLabel(source: "master" | "target" | "user_provided" | undefined) {
  if (source === "user_provided") return t`You provided`;
  if (source === "target") return t`This job version`;
  if (source === "master") return t`Existing resume`;
  return t`Resume evidence`;
}

function RecommendationMetadata({ recommendation }: { recommendation?: Props["data"]["recommendation"] }) {
  if (
    !recommendation ||
    (!recommendation.currentIssue &&
      !recommendation.recommendedAction &&
      !recommendation.riskOrGap &&
      !recommendation.factBoundary &&
      (recommendation.evidenceRefs?.length ?? 0) === 0)
  ) {
    return null;
  }

  return (
    <div className="min-w-0 space-y-2 rounded-md border bg-muted/20 p-3 text-sm">
      {recommendation.currentIssue ? (
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">
            <Trans>Current issue</Trans>
          </p>
          <p className="break-words">{recommendation.currentIssue}</p>
        </div>
      ) : null}
      {recommendation.recommendedAction ? (
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">
            <Trans>Recommended action</Trans>
          </p>
          <p className="break-words">{recommendation.recommendedAction}</p>
        </div>
      ) : null}
      {recommendation.riskOrGap ? (
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">
            <Trans>Risk or gap</Trans>
          </p>
          <p className="break-words">{recommendation.riskOrGap}</p>
        </div>
      ) : null}
      {recommendation.factBoundary ? (
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">
            <Trans>Fact boundary</Trans>
          </p>
          <p className="break-words">{recommendation.factBoundary}</p>
        </div>
      ) : null}
      <div className="min-w-0">
        <p className="text-xs font-medium text-muted-foreground">
          <Trans>Evidence</Trans>
        </p>
        {recommendation.evidenceRefs && recommendation.evidenceRefs.length > 0 ? (
          <ul className="list-disc space-y-1 ps-5 text-muted-foreground">
            {recommendation.evidenceRefs.map((evidence, index) => (
              <li key={`${evidence.kind}-${index}`} className="break-words">
                <span className="font-medium text-foreground">
                  {recommendationEvidenceSourceLabel(evidence.source)}
                </span>
                : {evidence.summary}
              </li>
            ))}
          </ul>
        ) : recommendation.semanticKind === "gap_notice" ? (
          <p className="break-words text-muted-foreground">
            <Trans>Evidence was not found in this resume.</Trans>
          </p>
        ) : (
          <p className="break-words text-muted-foreground">
            <Trans>Evidence linkage is unavailable.</Trans>
          </p>
        )}
      </div>
    </div>
  );
}

function createDraftHistory(initial: AiEditDraft): DraftHistory {
  return {
    past: [],
    present: structuredClone(initial),
    future: [],
  };
}

function pushDraft(history: DraftHistory, nextDraft: AiEditDraft): DraftHistory {
  if (isDeepEqual(history.present, nextDraft)) return history;

  return {
    past: [...history.past, history.present].slice(-maxDraftHistory),
    present: nextDraft,
    future: [],
  };
}

function getDialogTitle(snapshot: AiEditUiTargetSnapshot) {
  if (snapshot.target.type === "summary") return t`Update summary`;
  if (snapshot.target.type === "experience") return t`Update an existing experience`;
  return t`Update an existing project`;
}

function getContentHeading(snapshot: AiEditUiTargetSnapshot) {
  if (snapshot.target.type === "summary") return t`Summary content`;
  if (snapshot.target.type === "experience") return t`Experience content`;
  return t`Project content`;
}

function formatUrl(value: AiEditUrlDraft) {
  return value.label || value.url;
}

function AiFieldBadge() {
  return (
    <Badge variant="secondary" className="h-5 px-1.5 text-[0.65rem] leading-none">
      AI
    </Badge>
  );
}

function FieldHeading({ children, ai }: { children: React.ReactNode; ai?: boolean }) {
  return (
    <div className="flex min-h-5 items-center gap-2">
      <h3 className="text-sm font-medium text-foreground">{children}</h3>
      {ai && <AiFieldBadge />}
    </div>
  );
}

function EmptyContent() {
  return <span className="text-muted-foreground">{t`No content yet.`}</span>;
}

function ReadOnlyInputField({
  label,
  value,
  ai,
  className,
}: {
  label: React.ReactNode;
  value: string;
  ai?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("grid gap-2", className)}>
      <FieldHeading ai={ai}>{label}</FieldHeading>
      <Input
        value={value}
        readOnly
        aria-readonly
        placeholder={t`No content yet.`}
        className="bg-muted/40 text-muted-foreground"
      />
    </div>
  );
}

function EditableInputField({
  label,
  value,
  ai,
  onChange,
}: {
  label: React.ReactNode;
  value: string;
  ai?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <FieldHeading ai={ai}>{label}</FieldHeading>
      <Input value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function ReadOnlyRichTextField({
  label,
  value,
  ai,
  contentClassName,
}: {
  label: React.ReactNode;
  value: string;
  ai?: boolean;
  contentClassName?: string;
}) {
  return (
    <div className="grid gap-2">
      <FieldHeading ai={ai}>{label}</FieldHeading>
      <div className={cn("min-h-28 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground", contentClassName)}>
        {hasTextContent(value) ? <TiptapContent content={value} /> : <EmptyContent />}
      </div>
    </div>
  );
}

function EditableRichTextField({
  label,
  value,
  ai,
  onChange,
  editorClassName,
}: {
  label: React.ReactNode;
  value: string;
  ai?: boolean;
  onChange: (value: string) => void;
  editorClassName?: string;
}) {
  return (
    <div className="grid gap-2">
      <FieldHeading ai={ai}>{label}</FieldHeading>
      <RichInput
        value={value}
        onChange={onChange}
        className="min-h-0"
        editorClassName={cn("max-h-none!", editorClassName)}
        hideFullscreenButton
      />
    </div>
  );
}

function EditableUrlField({
  label,
  value,
  hideLabelButton,
  onChange,
}: {
  label: React.ReactNode;
  value: AiEditUrlDraft;
  hideLabelButton?: boolean;
  onChange: (value: AiEditUrlDraft) => void;
}) {
  return (
    <div className="grid gap-2">
      <FieldHeading>{label}</FieldHeading>
      <URLInput value={value} onChange={onChange} hideLabelButton={hideLabelButton} />
    </div>
  );
}

function ReadOnlySwitchField({ label, checked }: { label: React.ReactNode; checked: boolean }) {
  return (
    <div className="flex items-center gap-x-2">
      <Switch checked={checked} disabled />
      <Label className="mt-0! text-muted-foreground">{label}</Label>
    </div>
  );
}

function EditableSwitchField({
  label,
  checked,
  onCheckedChange,
}: {
  label: React.ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex min-h-10 items-center gap-x-2">
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
      <Label className="mt-0!">{label}</Label>
    </div>
  );
}

function HiddenInTargetSwitch({
  draft,
  disabled = false,
  onChangeDraft,
}: {
  draft: AiEditDraft;
  disabled?: boolean;
  onChangeDraft: (draft: AiEditDraft) => void;
}) {
  if (draft.type === "summary") return null;

  return (
    <div className="flex min-h-10 items-center gap-x-2">
      <Switch
        checked={draft.hidden}
        disabled={disabled}
        onCheckedChange={(hidden) => onChangeDraft({ ...draft, hidden })}
      />
      <Label className="mt-0!">
        <Trans>Hide in target</Trans>
      </Label>
    </div>
  );
}

function SummaryFields({
  draft,
  readonly = false,
  onChange,
}: {
  draft: Extract<AiEditDraft, { type: "summary" }>;
  readonly?: boolean;
  onChange?: (draft: AiEditDraft) => void;
}) {
  if (readonly) {
    return <ReadOnlyRichTextField label={<Trans>Content</Trans>} value={draft.content} ai />;
  }

  return (
    <div className="grid min-h-[18rem] gap-2">
      <FieldHeading ai>
        <Trans>Content</Trans>
      </FieldHeading>
      <RichInput
        value={draft.content}
        onChange={(value) => onChange?.({ ...draft, content: value })}
        className="min-h-0"
        editorClassName="min-h-[16rem]! max-h-none!"
        hideFullscreenButton
      />
    </div>
  );
}

function ExperienceFields({
  draft,
  readonly = false,
  onChange,
}: {
  draft: Extract<AiEditDraft, { type: "experience" }>;
  readonly?: boolean;
  onChange?: (draft: AiEditDraft) => void;
}) {
  const updateRole = (roleId: string, patch: Partial<AiEditRoleDraft>) => {
    onChange?.({
      ...draft,
      roles: draft.roles.map((role) => (role.id === roleId ? { ...role, ...patch } : role)),
    });
  };

  const addRole = () => {
    onChange?.({
      ...draft,
      roles: [...draft.roles, { id: generateId(), position: "", period: "", description: "" }],
    });
  };

  const removeRole = (roleId: string) => {
    onChange?.({
      ...draft,
      roles: draft.roles.filter((role) => role.id !== roleId),
    });
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {readonly ? (
        <>
          <ReadOnlyInputField label={<Trans>Company</Trans>} value={draft.company} />
          <ReadOnlyInputField label={<Trans>Location</Trans>} value={draft.location} />
          <ReadOnlyInputField label={<Trans>Position</Trans>} value={draft.position} ai />
          <ReadOnlyInputField label={<Trans>Period</Trans>} value={draft.period} />
          <ReadOnlyInputField label={<Trans>Website</Trans>} value={formatUrl(draft.website)} />
          <div className="self-end">
            <ReadOnlySwitchField label={<Trans>Show link in title</Trans>} checked={draft.options.showLinkInTitle} />
          </div>
        </>
      ) : (
        <>
          <div className="grid gap-2">
            <FieldHeading>
              <Trans>Company</Trans>
            </FieldHeading>
            <Input value={draft.company} onChange={(event) => onChange?.({ ...draft, company: event.target.value })} />
          </div>

          <div className="grid gap-2">
            <FieldHeading>
              <Trans>Location</Trans>
            </FieldHeading>
            <Input
              value={draft.location}
              onChange={(event) => onChange?.({ ...draft, location: event.target.value })}
            />
          </div>

          <div className="grid gap-2">
            <FieldHeading ai>
              <Trans>Position</Trans>
            </FieldHeading>
            <Input
              value={draft.position}
              onChange={(event) => onChange?.({ ...draft, position: event.target.value })}
            />
          </div>

          <div className="grid gap-2">
            <FieldHeading>
              <Trans>Period</Trans>
            </FieldHeading>
            <Input value={draft.period} onChange={(event) => onChange?.({ ...draft, period: event.target.value })} />
          </div>

          <div className="grid gap-2 sm:col-span-full">
            <FieldHeading>
              <Trans>Website</Trans>
            </FieldHeading>
            <URLInput
              value={draft.website}
              onChange={(website) => onChange?.({ ...draft, website })}
              hideLabelButton={draft.options.showLinkInTitle}
            />
          </div>

          <div className="flex items-center gap-x-2 sm:col-span-full">
            <Switch
              checked={draft.options.showLinkInTitle}
              onCheckedChange={(showLinkInTitle) =>
                onChange?.({ ...draft, options: { ...draft.options, showLinkInTitle } })
              }
            />
            <Label className="mt-0!">
              <Trans>Show link in title</Trans>
            </Label>
          </div>
        </>
      )}

      <div className="flex items-center justify-between gap-3 sm:col-span-full">
        <div className="space-y-1">
          <p className="font-medium text-foreground">
            <Trans>Role Progression</Trans>
          </p>
          {!readonly && (
            <p className="text-xs text-muted-foreground">
              <Trans>Add multiple roles to show career progression at the same company.</Trans>
            </p>
          )}
        </div>

        {!readonly && (
          <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={addRole}>
            <PlusIcon />
            <Trans>Add Role</Trans>
          </Button>
        )}
      </div>

      {draft.roles.length > 0 ? (
        <div className="grid gap-4 sm:col-span-full">
          {draft.roles.map((role, index) => (
            <div key={role.id} className={cn("grid rounded-md border", readonly && "bg-muted/20")}>
              <div className="flex items-center justify-between rounded-t bg-border/30 px-2 py-1.5">
                <div className="px-2 text-sm font-medium">
                  <Trans>Role {index + 1}</Trans>
                </div>

                {!readonly && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeRole(role.id)}
                  >
                    <TrashSimpleIcon />
                    <Trans>Remove</Trans>
                  </Button>
                )}
              </div>

              <div className="grid gap-4 p-4 sm:grid-cols-2">
                {readonly ? (
                  <>
                    <ReadOnlyInputField label={<Trans>Position</Trans>} value={role.position} ai />
                    <ReadOnlyInputField label={<Trans>Period</Trans>} value={role.period} />
                    <div className="sm:col-span-full">
                      <ReadOnlyRichTextField label={<Trans>Description</Trans>} value={role.description} ai />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid gap-2">
                      <FieldHeading ai>
                        <Trans>Position</Trans>
                      </FieldHeading>
                      <Input
                        value={role.position}
                        onChange={(event) => updateRole(role.id, { position: event.target.value })}
                      />
                    </div>

                    <div className="grid gap-2">
                      <FieldHeading>
                        <Trans>Period</Trans>
                      </FieldHeading>
                      <Input
                        value={role.period}
                        onChange={(event) => updateRole(role.id, { period: event.target.value })}
                      />
                    </div>

                    <div className="grid gap-2 sm:col-span-full">
                      <FieldHeading ai>
                        <Trans>Description</Trans>
                      </FieldHeading>
                      <RichInput
                        value={role.description}
                        onChange={(value) => updateRole(role.id, { description: value })}
                        editorClassName="min-h-[12rem]! max-h-none!"
                        hideFullscreenButton
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : readonly ? (
        <div className="sm:col-span-full">
          <ReadOnlyRichTextField label={<Trans>Description</Trans>} value={draft.description} ai />
        </div>
      ) : (
        <div className="grid gap-2 sm:col-span-full">
          <FieldHeading ai>
            <Trans>Description</Trans>
          </FieldHeading>
          <RichInput
            value={draft.description}
            onChange={(value) => onChange?.(setAiEditDraftMainHtml(draft, value))}
            editorClassName="min-h-[16rem]! max-h-none!"
            hideFullscreenButton
          />
        </div>
      )}
    </div>
  );
}

function ProjectFields({
  draft,
  readonly = false,
  onChange,
}: {
  draft: Extract<AiEditDraft, { type: "project" }>;
  readonly?: boolean;
  onChange?: (draft: AiEditDraft) => void;
}) {
  if (readonly) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <ReadOnlyInputField label={<Trans>Name</Trans>} value={draft.name} ai />
        <ReadOnlyInputField label={<Trans>Period</Trans>} value={draft.period} />
        <ReadOnlyInputField label={<Trans>Website</Trans>} value={formatUrl(draft.website)} />
        <div className="self-end">
          <ReadOnlySwitchField label={<Trans>Show link in title</Trans>} checked={draft.options.showLinkInTitle} />
        </div>
        <div className="sm:col-span-full">
          <ReadOnlyRichTextField label={<Trans>Description</Trans>} value={draft.description} ai />
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="grid gap-2">
        <FieldHeading ai>
          <Trans>Name</Trans>
        </FieldHeading>
        <Input value={draft.name} onChange={(event) => onChange?.({ ...draft, name: event.target.value })} />
      </div>

      <div className="grid gap-2">
        <FieldHeading>
          <Trans>Period</Trans>
        </FieldHeading>
        <Input value={draft.period} onChange={(event) => onChange?.({ ...draft, period: event.target.value })} />
      </div>

      <div className="grid gap-2 sm:col-span-full">
        <FieldHeading>
          <Trans>Website</Trans>
        </FieldHeading>
        <URLInput
          value={draft.website}
          onChange={(website) => onChange?.({ ...draft, website })}
          hideLabelButton={draft.options.showLinkInTitle}
        />
      </div>

      <div className="flex items-center gap-x-2 sm:col-span-full">
        <Switch
          checked={draft.options.showLinkInTitle}
          onCheckedChange={(showLinkInTitle) =>
            onChange?.({ ...draft, options: { ...draft.options, showLinkInTitle } })
          }
        />
        <Label className="mt-0!">
          <Trans>Show link in title</Trans>
        </Label>
      </div>

      <div className="grid gap-2 sm:col-span-full">
        <FieldHeading ai>
          <Trans>Description</Trans>
        </FieldHeading>
        <RichInput
          value={getAiEditDraftMainHtml(draft)}
          onChange={(value) => onChange?.(setAiEditDraftMainHtml(draft, value))}
          editorClassName="min-h-[16rem]! max-h-none!"
          hideFullscreenButton
        />
      </div>
    </div>
  );
}

function DraftFields({
  draft,
  readonly = false,
  onChange,
}: {
  draft: AiEditDraft;
  readonly?: boolean;
  onChange?: (draft: AiEditDraft) => void;
}) {
  if (draft.type === "summary") return <SummaryFields draft={draft} readonly={readonly} onChange={onChange} />;
  if (draft.type === "experience") return <ExperienceFields draft={draft} readonly={readonly} onChange={onChange} />;
  return <ProjectFields draft={draft} readonly={readonly} onChange={onChange} />;
}

function OptimizationSuggestions({
  response,
  hasGeneratedResult,
  isPending,
  recommendation,
  className,
}: {
  response: AiEditResponseState;
  hasGeneratedResult: boolean;
  isPending: boolean;
  recommendation?: Props["data"]["recommendation"];
  className?: string;
}) {
  const suggestions = response.suggestions.length > 0 ? response.suggestions : response.reasons;

  return (
    <div className={cn("grid content-start gap-2", className)}>
      <FieldHeading>
        <Trans>Optimization suggestions</Trans>
      </FieldHeading>

      <RecommendationMetadata recommendation={recommendation} />

      {suggestions.length > 0 ? (
        <ul className="grid list-disc gap-1.5 ps-5 text-sm text-muted-foreground">
          {suggestions.map((suggestion, index) => (
            <li key={index}>{suggestion}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {isPending ? (
            <Trans>Generating suggestions...</Trans>
          ) : hasGeneratedResult ? (
            <Trans>No optimization suggestions were returned.</Trans>
          ) : (
            <Trans>AI suggestions will appear after generation.</Trans>
          )}
        </p>
      )}
    </div>
  );
}

function ComparisonRow({
  left,
  right,
  className,
}: {
  left: React.ReactNode;
  right: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("grid min-w-0 items-start gap-4 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1fr)]", className)}>
      <div className="min-w-0">{left}</div>
      <div className="min-w-0 lg:pl-5">{right}</div>
    </div>
  );
}

function DefaultEditPane({
  snapshot,
  draft,
  recommendation,
  onChangeDraft,
  onViewRecommendation,
  onOneClickOptimize,
  onTuneDirection,
  onAcceptHiddenRecommendation,
  onIgnoreRecommendation,
  isProcessingRecommendation,
  quotaCost,
  isReviewingRecommendation,
  onRegenerate,
}: {
  snapshot: AiEditUiTargetSnapshot;
  draft: AiEditDraft;
  recommendation?: Props["data"]["recommendation"];
  onChangeDraft: (draft: AiEditDraft) => void;
  onViewRecommendation: () => void;
  onOneClickOptimize: () => void;
  onTuneDirection: () => void;
  onAcceptHiddenRecommendation: () => void;
  onIgnoreRecommendation: () => void;
  isProcessingRecommendation: boolean;
  quotaCost: number;
  isReviewingRecommendation: boolean;
  onRegenerate: () => void;
}) {
  const isHiddenItem = draft.type !== "summary" && draft.hidden;
  const canViewRecommendation = hasReviewableRecommendation(recommendation);
  const canHandleHideRecommendation = draft.type !== "summary" && isHideInTargetRecommendation(recommendation);
  const disabledAiTitle = isHiddenItem ? t`Unhide this item before optimizing` : undefined;
  const reason = recommendation?.reasons?.[0];

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="grid gap-2">
          <h2 className="text-base font-semibold text-foreground">{getContentHeading(snapshot)}</h2>
          {isHiddenItem && reason ? <p className="text-sm text-muted-foreground">{reason}</p> : null}
          {isHiddenItem ? (
            <p className="text-sm text-muted-foreground">
              <Trans>Unhide this item before optimizing</Trans>
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <HiddenInTargetSwitch draft={draft} onChangeDraft={onChangeDraft} />

          <div className="flex flex-wrap items-center justify-end gap-2">
            {isReviewingRecommendation ? (
              <>
                <Button type="button" disabled={isHiddenItem} title={disabledAiTitle} onClick={onRegenerate}>
                  <MagicWandIcon />
                  <Trans>Regenerate</Trans>
                </Button>
              </>
            ) : canViewRecommendation ? (
              <Button type="button" disabled={isHiddenItem} title={disabledAiTitle} onClick={onViewRecommendation}>
                <MagicWandIcon />
                <Trans>View AI suggestion</Trans>
              </Button>
            ) : (
              <Button type="button" disabled={isHiddenItem} title={disabledAiTitle} onClick={onOneClickOptimize}>
                <MagicWandIcon />
                <Trans>AI suggestion</Trans>
                {!isHiddenItem ? <span aria-hidden="true">◈ {quotaCost}</span> : null}
              </Button>
            )}

            <Button
              type="button"
              variant="outline"
              disabled={isHiddenItem}
              title={disabledAiTitle}
              onClick={onTuneDirection}
            >
              <Trans>Tune suggestion</Trans>
            </Button>
          </div>
        </div>
      </div>

      <RecommendationMetadata recommendation={recommendation} />

      {canHandleHideRecommendation ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={isProcessingRecommendation}
            onClick={onIgnoreRecommendation}
          >
            <Trans>Ignore this suggestion</Trans>
          </Button>
          <Button type="button" disabled={isProcessingRecommendation} onClick={onAcceptHiddenRecommendation}>
            <Trans>Confirm hiding</Trans>
          </Button>
        </div>
      ) : null}

      <DraftFields draft={draft} readonly={isHiddenItem} onChange={onChangeDraft} />
    </section>
  );
}

function SummaryComparisonRows({
  originalDraft,
  draft,
  onChangeDraft,
}: {
  originalDraft: Extract<AiEditDraft, { type: "summary" }>;
  draft: Extract<AiEditDraft, { type: "summary" }>;
  onChangeDraft: (draft: AiEditDraft) => void;
}) {
  return (
    <ComparisonRow
      left={
        <ReadOnlyRichTextField
          label={<Trans>Content</Trans>}
          value={originalDraft.content}
          ai
          contentClassName="min-h-[16rem]"
        />
      }
      right={
        <EditableRichTextField
          label={<Trans>Content</Trans>}
          value={draft.content}
          ai
          editorClassName="min-h-[16rem]!"
          onChange={(value) => onChangeDraft({ ...draft, content: value })}
        />
      }
    />
  );
}

function ExperienceComparisonRows({
  originalDraft,
  draft,
  onChangeDraft,
}: {
  originalDraft: Extract<AiEditDraft, { type: "experience" }>;
  draft: Extract<AiEditDraft, { type: "experience" }>;
  onChangeDraft: (draft: AiEditDraft) => void;
}) {
  const updateRole = (roleId: string, patch: Partial<AiEditRoleDraft>) => {
    onChangeDraft({
      ...draft,
      roles: draft.roles.map((role) => (role.id === roleId ? { ...role, ...patch } : role)),
    });
  };

  const roleIds = Array.from(
    new Set([...originalDraft.roles.map((role) => role.id), ...draft.roles.map((role) => role.id)]),
  );

  return (
    <>
      <ComparisonRow
        left={
          <div className="grid gap-4 sm:grid-cols-2">
            <ReadOnlyInputField label={<Trans>Company</Trans>} value={originalDraft.company} />
            <ReadOnlyInputField label={<Trans>Location</Trans>} value={originalDraft.location} />
          </div>
        }
        right={
          <div className="grid gap-4 sm:grid-cols-2">
            <EditableInputField
              label={<Trans>Company</Trans>}
              value={draft.company}
              onChange={(company) => onChangeDraft({ ...draft, company })}
            />
            <EditableInputField
              label={<Trans>Location</Trans>}
              value={draft.location}
              onChange={(location) => onChangeDraft({ ...draft, location })}
            />
          </div>
        }
      />

      <ComparisonRow
        left={
          <div className="grid gap-4 sm:grid-cols-2">
            <ReadOnlyInputField label={<Trans>Position</Trans>} value={originalDraft.position} ai />
            <ReadOnlyInputField label={<Trans>Period</Trans>} value={originalDraft.period} />
          </div>
        }
        right={
          <div className="grid gap-4 sm:grid-cols-2">
            <EditableInputField
              label={<Trans>Position</Trans>}
              value={draft.position}
              ai
              onChange={(position) => onChangeDraft({ ...draft, position })}
            />
            <EditableInputField
              label={<Trans>Period</Trans>}
              value={draft.period}
              onChange={(period) => onChangeDraft({ ...draft, period })}
            />
          </div>
        }
      />

      <ComparisonRow
        left={<ReadOnlyInputField label={<Trans>Website</Trans>} value={formatUrl(originalDraft.website)} />}
        right={
          <EditableUrlField
            label={<Trans>Website</Trans>}
            value={draft.website}
            hideLabelButton={draft.options.showLinkInTitle}
            onChange={(website) => onChangeDraft({ ...draft, website })}
          />
        }
      />

      <ComparisonRow
        left={
          <ReadOnlySwitchField
            label={<Trans>Show link in title</Trans>}
            checked={originalDraft.options.showLinkInTitle}
          />
        }
        right={
          <EditableSwitchField
            label={<Trans>Show link in title</Trans>}
            checked={draft.options.showLinkInTitle}
            onCheckedChange={(showLinkInTitle) =>
              onChangeDraft({ ...draft, options: { ...draft.options, showLinkInTitle } })
            }
          />
        }
      />

      <ComparisonRow
        className="pt-2"
        left={
          <p className="font-medium text-foreground">
            <Trans>Role Progression</Trans>
          </p>
        }
        right={
          <p className="font-medium text-foreground">
            <Trans>Role Progression</Trans>
          </p>
        }
      />

      {roleIds.length > 0 ? (
        roleIds.map((roleId, index) => {
          const originalRole = originalDraft.roles.find((role) => role.id === roleId);
          const draftRole = draft.roles.find((role) => role.id === roleId);

          return (
            <ComparisonRow
              key={roleId}
              left={
                <div className="grid rounded-md border bg-muted/20">
                  <div className="rounded-t bg-border/30 px-4 py-1.5 text-sm font-medium text-muted-foreground">
                    <Trans>Role {index + 1}</Trans>
                  </div>

                  <div className="grid gap-4 p-4 sm:grid-cols-2">
                    <ReadOnlyInputField label={<Trans>Position</Trans>} value={originalRole?.position ?? ""} ai />
                    <ReadOnlyInputField label={<Trans>Period</Trans>} value={originalRole?.period ?? ""} />
                    <div className="sm:col-span-full">
                      <ReadOnlyRichTextField
                        label={<Trans>Description</Trans>}
                        value={originalRole?.description ?? ""}
                        ai
                        contentClassName="min-h-[12rem]"
                      />
                    </div>
                  </div>
                </div>
              }
              right={
                <div className="grid rounded-md border">
                  <div className="rounded-t bg-border/30 px-4 py-1.5 text-sm font-medium text-muted-foreground">
                    <Trans>Role {index + 1}</Trans>
                  </div>

                  <div className="grid gap-4 p-4 sm:grid-cols-2">
                    <EditableInputField
                      label={<Trans>Position</Trans>}
                      value={draftRole?.position ?? ""}
                      ai
                      onChange={(position) => updateRole(roleId, { position })}
                    />
                    <EditableInputField
                      label={<Trans>Period</Trans>}
                      value={draftRole?.period ?? ""}
                      onChange={(period) => updateRole(roleId, { period })}
                    />
                    <div className="sm:col-span-full">
                      <EditableRichTextField
                        label={<Trans>Description</Trans>}
                        value={draftRole?.description ?? ""}
                        ai
                        editorClassName="min-h-[12rem]!"
                        onChange={(description) => updateRole(roleId, { description })}
                      />
                    </div>
                  </div>
                </div>
              }
            />
          );
        })
      ) : (
        <ComparisonRow
          left={
            <ReadOnlyRichTextField
              label={<Trans>Description</Trans>}
              value={originalDraft.description}
              ai
              contentClassName="min-h-[16rem]"
            />
          }
          right={
            <EditableRichTextField
              label={<Trans>Description</Trans>}
              value={draft.description}
              ai
              editorClassName="min-h-[16rem]!"
              onChange={(value) => onChangeDraft(setAiEditDraftMainHtml(draft, value))}
            />
          }
        />
      )}
    </>
  );
}

function ProjectComparisonRows({
  originalDraft,
  draft,
  onChangeDraft,
}: {
  originalDraft: Extract<AiEditDraft, { type: "project" }>;
  draft: Extract<AiEditDraft, { type: "project" }>;
  onChangeDraft: (draft: AiEditDraft) => void;
}) {
  return (
    <>
      <ComparisonRow
        left={
          <div className="grid gap-4 sm:grid-cols-2">
            <ReadOnlyInputField label={<Trans>Name</Trans>} value={originalDraft.name} ai />
            <ReadOnlyInputField label={<Trans>Period</Trans>} value={originalDraft.period} />
          </div>
        }
        right={
          <div className="grid gap-4 sm:grid-cols-2">
            <EditableInputField
              label={<Trans>Name</Trans>}
              value={draft.name}
              ai
              onChange={(name) => onChangeDraft({ ...draft, name })}
            />
            <EditableInputField
              label={<Trans>Period</Trans>}
              value={draft.period}
              onChange={(period) => onChangeDraft({ ...draft, period })}
            />
          </div>
        }
      />

      <ComparisonRow
        left={<ReadOnlyInputField label={<Trans>Website</Trans>} value={formatUrl(originalDraft.website)} />}
        right={
          <EditableUrlField
            label={<Trans>Website</Trans>}
            value={draft.website}
            hideLabelButton={draft.options.showLinkInTitle}
            onChange={(website) => onChangeDraft({ ...draft, website })}
          />
        }
      />

      <ComparisonRow
        left={
          <ReadOnlySwitchField
            label={<Trans>Show link in title</Trans>}
            checked={originalDraft.options.showLinkInTitle}
          />
        }
        right={
          <EditableSwitchField
            label={<Trans>Show link in title</Trans>}
            checked={draft.options.showLinkInTitle}
            onCheckedChange={(showLinkInTitle) =>
              onChangeDraft({ ...draft, options: { ...draft.options, showLinkInTitle } })
            }
          />
        }
      />

      <ComparisonRow
        left={
          <ReadOnlyRichTextField
            label={<Trans>Description</Trans>}
            value={originalDraft.description}
            ai
            contentClassName="min-h-[16rem]"
          />
        }
        right={
          <EditableRichTextField
            label={<Trans>Description</Trans>}
            value={draft.description}
            ai
            editorClassName="min-h-[16rem]!"
            onChange={(value) => onChangeDraft(setAiEditDraftMainHtml(draft, value))}
          />
        }
      />
    </>
  );
}

function ComparisonDraftRows({
  originalDraft,
  draft,
  readonly = false,
  onChangeDraft,
}: {
  originalDraft: AiEditDraft;
  draft: AiEditDraft;
  readonly?: boolean;
  onChangeDraft: (draft: AiEditDraft) => void;
}) {
  if (readonly) {
    return (
      <ComparisonRow
        left={<DraftFields draft={originalDraft} readonly />}
        right={<DraftFields draft={draft} readonly />}
      />
    );
  }

  if (originalDraft.type === "summary" && draft.type === "summary") {
    return <SummaryComparisonRows originalDraft={originalDraft} draft={draft} onChangeDraft={onChangeDraft} />;
  }

  if (originalDraft.type === "experience" && draft.type === "experience") {
    return <ExperienceComparisonRows originalDraft={originalDraft} draft={draft} onChangeDraft={onChangeDraft} />;
  }

  if (originalDraft.type === "project" && draft.type === "project") {
    return <ProjectComparisonRows originalDraft={originalDraft} draft={draft} onChangeDraft={onChangeDraft} />;
  }

  return null;
}

function AiExpandedComparisonPane({
  mode,
  originalDraft,
  draft,
  response,
  hasGeneratedResult,
  isPending,
  disableAiInputs,
  customPrompt,
  recommendation,
  canUndo,
  canRedo,
  onChangeCustomPrompt,
  onChangeDraft,
  onChangeHidden,
  onGenerate,
  onUndo,
  onRedo,
  quotaCost,
}: {
  mode: AiPanelMode;
  originalDraft: AiEditDraft;
  draft: AiEditDraft;
  response: AiEditResponseState;
  hasGeneratedResult: boolean;
  isPending: boolean;
  disableAiInputs: boolean;
  customPrompt: string;
  recommendation?: Props["data"]["recommendation"];
  canUndo: boolean;
  canRedo: boolean;
  onChangeCustomPrompt: (value: string) => void;
  onChangeDraft: (draft: AiEditDraft) => void;
  onChangeHidden: (draft: AiEditDraft) => void;
  onGenerate: () => void;
  onUndo: () => void;
  onRedo: () => void;
  quotaCost: number;
}) {
  const generateLabel =
    hasGeneratedResult || mode === "generated" ? <Trans>Regenerate</Trans> : <Trans>Generate</Trans>;

  return (
    <section className="grid min-w-0 gap-4">
      <ComparisonRow
        left={
          <h2 className="text-base font-semibold text-foreground">
            <Trans>Original content</Trans>
          </h2>
        }
        right={
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-foreground">
              <Trans>AI optimized content</Trans>
            </h2>

            <div className="flex items-center justify-end gap-1">
              <HiddenInTargetSwitch draft={draft} onChangeDraft={onChangeHidden} />

              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                disabled={disableAiInputs || !canUndo}
                title={t`Undo`}
                onClick={onUndo}
              >
                <ArrowCounterClockwiseIcon />
              </Button>

              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                disabled={disableAiInputs || !canRedo}
                title={t`Redo`}
                onClick={onRedo}
              >
                <ArrowClockwiseIcon />
              </Button>

              <Button type="button" disabled={disableAiInputs || isPending} onClick={onGenerate}>
                <MagicWandIcon />
                {isPending ? <Trans>Generating...</Trans> : generateLabel}
                <span aria-hidden="true">◈ {quotaCost}</span>
              </Button>
            </div>
          </div>
        }
      />

      <ComparisonRow
        left={
          <OptimizationSuggestions
            response={response}
            hasGeneratedResult={hasGeneratedResult}
            isPending={isPending}
            recommendation={recommendation}
            className="min-h-32"
          />
        }
        right={
          <div className="grid min-h-32 content-start gap-2">
            <FieldHeading>
              <Trans>Optimization direction</Trans>
            </FieldHeading>
            <Textarea
              id="ai-edit-custom-prompt"
              value={customPrompt}
              disabled={disableAiInputs}
              onChange={(event) => onChangeCustomPrompt(event.target.value)}
              placeholder={t`Leave blank for automatic AI suggestions, or add a direction such as: make it more result-oriented; turn it into three bullets; emphasize a specific capability.`}
              className="max-h-32 min-h-20 resize-y"
            />
          </div>
        }
      />

      <div className="relative grid gap-4 lg:before:absolute lg:before:inset-y-0 lg:before:left-[calc((100%_-_1rem)*0.4871794872_+_1rem)] lg:before:w-px lg:before:bg-border/80">
        <ComparisonDraftRows
          originalDraft={originalDraft}
          draft={draft}
          readonly={disableAiInputs}
          onChangeDraft={onChangeDraft}
        />
      </div>
    </section>
  );
}

export function AiEditDialog({ data }: Props) {
  const { resumeId } = useParams({ from: "/builder/$resumeId" });
  const queryClient = useQueryClient();
  const { runWithQuotaPreflight } = useQuotaPreflight();
  const confirm = useConfirm();
  const resumeData = useResumeStore((state) => state.resume.data);
  const replaceResumeDataFromServer = useResumeStore((state) => state.replaceResumeDataFromServer);
  const updateResumeData = useResumeStore((state) => state.updateResumeData);
  const closeDialog = useDialogStore((state) => state.closeDialog);

  const [resumeDataSnapshot] = useState(resumeData);
  const recommendationKey = data.recommendation
    ? `${data.recommendation.planId ?? ""}:${data.recommendation.itemId}`
    : null;
  const [detachedRecommendationKey, setDetachedRecommendationKey] = useState<string | null>(null);
  const activeRecommendation = recommendationKey === detachedRecommendationKey ? undefined : data.recommendation;
  const [aiMode, setAiMode] = useState<AiPanelMode>(
    shouldPreloadDraftOnMount(data.target, activeRecommendation) ? "generated" : "default",
  );
  const [comparisonDraft, setComparisonDraft] = useState<AiEditDraft | null>(null);
  const [customPrompt, setCustomPrompt] = useState(data.guidedReview?.note ?? "");
  const [response, setResponse] = useState<AiEditResponseState>(() =>
    createAiEditResponseFromRecommendation(activeRecommendation),
  );
  const [hasGeneratedResult, setHasGeneratedResult] = useState(false);
  const autoRegenerateStartedRef = useRef(false);
  const retryableGenerationRef = useRef<OptimizeMutationInput | null>(null);
  const guidedApplyAttemptRef = useRef<{ operationId: string; data: ResumeData } | null>(null);

  const snapshot = useMemo(() => {
    try {
      return getAiEditUiTargetSnapshot(resumeDataSnapshot, data.target);
    } catch {
      return null;
    }
  }, [data.target, resumeDataSnapshot]);

  const targetKey = snapshot ? getAiEditTargetKey(snapshot.target) : "missing";
  const quotaAction = snapshot ? getAiEditQuotaAction(snapshot.target) : "phase5.item_regenerate.v1";
  const quotaCost = getAiActionCost({ action: quotaAction }).requiredUnits;
  const [draftHistory, setDraftHistory] = useState(() =>
    createDraftHistory(
      snapshot ? createInitialDraft(snapshot.original, data.target, activeRecommendation) : emptyAiEditDraft,
    ),
  );
  const draft = draftHistory.present;
  const isAiExpanded = aiMode !== "default";
  const isHiddenInTarget = draft.type !== "summary" && draft.hidden;
  const isReviewingRecommendation = isAiExpanded && hasReviewableRecommendation(activeRecommendation);
  const isPickupReview = Boolean(data.pickupReview);
  const hasDraftContent = hasAiEditDraftRequiredContent(draft);
  const hasEditedDraft = snapshot ? !isDeepEqual(draft, snapshot.original) : false;
  const requiresSuccessfulProposalRegeneration =
    data.requestedOperation === "regenerate" && Boolean(activeRecommendation?.planId);
  const canApply =
    snapshot !== null &&
    hasDraftContent &&
    hasEditedDraft &&
    (!isAiExpanded ||
      hasGeneratedResult ||
      (!requiresSuccessfulProposalRegeneration && hasAiEditDraftAiEligibleContent(draft)));

  useEffect(() => {
    const original = snapshot?.original ?? emptyAiEditDraft;
    const shouldPreload = shouldPreloadDraftOnMount(data.target, activeRecommendation);

    setDraftHistory(createDraftHistory(createInitialDraft(original, data.target, activeRecommendation)));
    setComparisonDraft(shouldPreload ? structuredClone(original) : null);
    setResponse(createAiEditResponseFromRecommendation(activeRecommendation));
    setCustomPrompt(data.guidedReview?.note ?? "");
    setHasGeneratedResult(shouldPreload);
    setAiMode(shouldPreload ? "generated" : "default");
    autoRegenerateStartedRef.current = false;
    retryableGenerationRef.current = null;
  }, [data.guidedReview?.note, activeRecommendation, data.target, snapshot?.original, targetKey]);

  const commitDraft = (nextDraft: AiEditDraft) => {
    setDraftHistory((history) => pushDraft(history, nextDraft));
  };

  const undoDraft = () => {
    setDraftHistory((history) => {
      const previous = history.past.at(-1);
      if (!previous) return history;

      return {
        past: history.past.slice(0, -1),
        present: previous,
        future: [history.present, ...history.future].slice(0, maxDraftHistory),
      };
    });
  };

  const redoDraft = () => {
    setDraftHistory((history) => {
      const next = history.future[0];
      if (!next) return history;

      return {
        past: [...history.past, history.present].slice(-maxDraftHistory),
        present: next,
        future: history.future.slice(1),
      };
    });
  };

  const { mutateAsync: optimizeSection, isPending } = useMutation({
    mutationFn: ({
      operationId,
      requestedOperation,
      requestDraft,
      originalDraft,
      customPrompt,
    }: OptimizeMutationInput) => {
      if (!snapshot) throw new Error(t`The AI edit target could not be found.`);

      return client.multiResumeAi.optimizeSection({
        operationId,
        resumeId,
        target: snapshot.target,
        resumeData: resumeDataSnapshot,
        originalTarget: toAiEditResult(originalDraft),
        currentDraft: toAiEditResult(requestDraft),
        customPrompt,
        requestedOperation,
        ...(requestedOperation === "regenerate" && activeRecommendation?.planId
          ? {
              proposal: {
                planId: activeRecommendation.planId,
                sectionId: snapshot.target.type === "project" ? "projects" : snapshot.target.type,
                itemId: activeRecommendation.itemId,
              },
            }
          : {}),
      });
    },
  });
  const { mutateAsync: applyGuidedReviewProposal } = useMutation(
    orpc.multiResumeWorkspace.applyGuidedReviewProposal.mutationOptions(),
  );
  const { mutateAsync: markOptimizationPlanItemIgnored, isPending: isIgnoringRecommendation } = useMutation(
    orpc.multiResumeWorkspace.markOptimizationPlanItemIgnored.mutationOptions(),
  );
  const { mutateAsync: applyPickupCandidate, isPending: isApplyingPickup } = useMutation(
    orpc.multiResumeWorkspace.applyPickupCandidate.mutationOptions(),
  );
  const pickupAttemptRef = useRef<{
    input: RouterInput["multiResumeWorkspace"]["applyPickupCandidate"];
    draft: AiEditResult;
  } | null>(null);
  const pickupActionLockRef = useRef(false);
  const pickupNavigationPromptPendingRef = useRef(false);
  const pickupDraftBaseline = useMemo(
    () => (snapshot ? createInitialDraft(snapshot.original, data.target, activeRecommendation) : emptyAiEditDraft),
    [activeRecommendation, data.target, snapshot],
  );
  const pickupDraftDirty = isPickupReview && !isDeepEqual(draft, pickupDraftBaseline);
  const pickupMutationPending = isPickupReview && (isApplyingPickup || pickupActionLockRef.current);

  const requestPickupClose = useCallback(async () => {
    if (!isPickupReview) {
      closeDialog();
      return;
    }
    if (pickupMutationPending || pickupActionLockRef.current || pickupNavigationPromptPendingRef.current) return;
    if (!pickupDraftDirty) {
      closeDialog();
      return;
    }

    pickupNavigationPromptPendingRef.current = true;
    try {
      const confirmed = await confirm(t`Leave this pickup review?`, {
        description: t`Your edited Complete Resume update will be lost.`,
        confirmText: t`Leave`,
        cancelText: t`Stay`,
      });
      if (confirmed) (closeDialog as (force?: boolean) => void)(true);
    } finally {
      pickupNavigationPromptPendingRef.current = false;
    }
  }, [closeDialog, confirm, isPickupReview, pickupDraftDirty, pickupMutationPending]);

  useBlocker({
    shouldBlockFn: async () => {
      if (!isPickupReview) return false;
      if (pickupMutationPending || pickupActionLockRef.current) return true;
      if (!pickupDraftDirty || pickupNavigationPromptPendingRef.current) return pickupDraftDirty;

      pickupNavigationPromptPendingRef.current = true;
      try {
        return !(await confirm(t`Leave this pickup review?`, {
          description: t`Your edited Complete Resume update will be lost.`,
          confirmText: t`Leave`,
          cancelText: t`Stay`,
        }));
      } finally {
        pickupNavigationPromptPendingRef.current = false;
      }
    },
    enableBeforeUnload: () => pickupMutationPending || pickupDraftDirty || pickupActionLockRef.current,
  });

  useDialogCloseGuard(() => {
    if (!isPickupReview) return true;
    if (pickupMutationPending || pickupActionLockRef.current) return false;
    return pickupDraftDirty ? "confirm" : true;
  });

  const invalidateOptimizationCaches = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: orpc.multiResumeWorkspace.editorContext.queryKey({ input: { resumeId } }),
        refetchType: "all",
      }),
      queryClient.invalidateQueries({
        queryKey: orpc.multiResumeWorkspace.state.queryKey(),
        refetchType: "all",
      }),
      queryClient.invalidateQueries({
        queryKey: orpc.resume.getById.queryKey({ input: { id: resumeId } }),
        refetchType: "all",
      }),
      queryClient.invalidateQueries({
        queryKey: orpc.multiResumeWorkspace.getJdWorkflow.queryKey({ input: { targetResumeId: resumeId } }),
        refetchType: "all",
      }),
    ]);
  };

  const generateAiResult = async (
    options: {
      requestDraft?: AiEditDraft;
      originalDraft?: AiEditDraft;
      customPrompt?: string;
      requestedOperation?: "generate" | "regenerate";
      resetHistoryFromBase?: boolean;
    } = {},
  ) => {
    if (!snapshot) return;

    const requestDraft = options.requestDraft ?? draft;
    const originalDraft = options.originalDraft ?? comparisonDraft ?? snapshot.original;
    const requestedOperation =
      options.requestedOperation ??
      retryableGenerationRef.current?.requestedOperation ??
      (hasGeneratedResult || data.requestedOperation === "regenerate" ? "regenerate" : "generate");
    const prompt = options.customPrompt ?? customPrompt;

    await runWithQuotaPreflight(
      {
        action: quotaAction,
        actionName: requestedOperation === "generate" ? t`AI suggestion` : t`Regenerate`,
      },
      async () => {
        const toastId = toast.loading(
          requestedOperation === "generate"
            ? t`Generating AI optimized content...`
            : t`Regenerating AI optimized content...`,
        );

        const retryable = retryableGenerationRef.current;
        const request =
          retryable &&
          retryable.requestedOperation === requestedOperation &&
          retryable.customPrompt === prompt &&
          isDeepEqual(retryable.requestDraft, requestDraft) &&
          isDeepEqual(retryable.originalDraft, originalDraft)
            ? retryable
            : {
                operationId: generateId(),
                requestedOperation,
                requestDraft: structuredClone(requestDraft),
                originalDraft: structuredClone(originalDraft),
                customPrompt: prompt,
              };
        retryableGenerationRef.current = request;

        try {
          const result = await optimizeSection(request);
          const nextDraft = mergeAiEditResultIntoDraft(requestDraft, result.result);

          if (options.resetHistoryFromBase) {
            setDraftHistory(pushDraft(createDraftHistory(requestDraft), nextDraft));
          } else {
            commitDraft(nextDraft);
          }

          setResponse({
            suggestions: result.suggestions,
            reasons: result.reasons,
          });
          setHasGeneratedResult(true);
          setAiMode("generated");
          if (requestedOperation === "regenerate" && activeRecommendation?.planId) {
            // The regenerated revision is durable proposal authority. Refresh
            // the underlying Guided Review view now so closing this dialog
            // never restores the pre-regeneration draft from query cache.
            await invalidateOptimizationCaches();
          }
          retryableGenerationRef.current = null;
          toast.success(t`AI optimized content generated.`, { id: toastId });
        } catch (error: unknown) {
          if (requiresNewRegenerationOperation(error)) retryableGenerationRef.current = null;
          const message = getUserFacingError(error, t`Could not generate AI optimized content.`);
          toast.error(message, { id: toastId });
        }
      },
    );
  };

  useEffect(() => {
    if (data.requestedOperation !== "regenerate" || autoRegenerateStartedRef.current || !snapshot) return;
    autoRegenerateStartedRef.current = true;
    const frozenDraft = structuredClone(draft);
    const prompt = data.guidedReview?.note ?? "";
    setAiMode("generated");
    setComparisonDraft(structuredClone(snapshot.original));
    setResponse(emptyAiEditResponse);
    setCustomPrompt(prompt);
    setHasGeneratedResult(false);
    setDraftHistory(createDraftHistory(frozenDraft));
    void generateAiResult({
      requestDraft: frozenDraft,
      // The editable proposal is the current regeneration draft, while the
      // server must compare the original target to the saved resume snapshot.
      // Sending the proposal for both values makes a valid Guided Review
      // regeneration fail its durable authority check before the provider.
      originalDraft: structuredClone(snapshot.original),
      customPrompt: prompt,
      requestedOperation: "regenerate",
      resetHistoryFromBase: true,
    });
  }, [data.guidedReview?.note, data.requestedOperation, snapshot, targetKey]);

  const openOneClickOptimize = () => {
    if (isHiddenInTarget) return;

    const frozenDraft = structuredClone(draft);

    setAiMode("generated");
    setComparisonDraft(frozenDraft);
    setResponse(emptyAiEditResponse);
    setCustomPrompt("");
    setHasGeneratedResult(false);
    setDraftHistory(createDraftHistory(frozenDraft));

    void generateAiResult({
      requestDraft: frozenDraft,
      originalDraft: frozenDraft,
      customPrompt: "",
      requestedOperation: "generate",
      resetHistoryFromBase: true,
    });
  };

  const openTuneDirection = () => {
    if (isHiddenInTarget) return;

    const frozenDraft = structuredClone(draft);

    setAiMode("tune");
    setComparisonDraft(frozenDraft);
    setResponse(emptyAiEditResponse);
    setCustomPrompt("");
    setHasGeneratedResult(false);
    setDraftHistory(createDraftHistory(createAiEditTuneDraft(frozenDraft)));
    window.setTimeout(() => document.getElementById("ai-edit-custom-prompt")?.focus(), 0);
  };

  const openRecommendationReview = () => {
    if (!snapshot || !hasReviewableRecommendation(activeRecommendation) || isHiddenInTarget) return;

    const original = structuredClone(snapshot.original);
    const nextDraft = createDraftFromRecommendation(original, activeRecommendation);

    setAiMode("generated");
    setComparisonDraft(original);
    setResponse(createAiEditResponseFromRecommendation(activeRecommendation));
    setCustomPrompt("");
    setHasGeneratedResult(true);
    setDraftHistory(createDraftHistory(nextDraft));
  };

  const openManualEdit = () => {
    if (!snapshot) return;

    const original = structuredClone(snapshot.original);

    setAiMode("default");
    setComparisonDraft(null);
    setResponse(createAiEditResponseFromRecommendation(activeRecommendation));
    setCustomPrompt("");
    setHasGeneratedResult(false);
    setDraftHistory(createDraftHistory(original));
  };

  const applyResult = async (draftToApply: AiEditDraft = draft) => {
    if (!snapshot) return;

    if (data.pickupReview) {
      if (pickupActionLockRef.current) return;
      pickupActionLockRef.current = true;
      const editedDraft = toAiEditResult(draftToApply);
      const previousAttempt = pickupAttemptRef.current;
      const input =
        previousAttempt && isDeepEqual(previousAttempt.draft, editedDraft)
          ? previousAttempt.input
          : {
              completeResumeId: data.pickupReview.completeResumeId,
              candidateId: data.pickupReview.candidateId,
              expectedTargetResumeFingerprint: data.pickupReview.expectedTargetResumeFingerprint,
              expectedMasterResumeFingerprint: data.pickupReview.expectedMasterResumeFingerprint,
              decision: "apply" as const,
              operationId: generateId(),
              editedDraft,
            };
      pickupAttemptRef.current = { input, draft: structuredClone(editedDraft) };
      try {
        const result = await applyPickupCandidate(input);
        if (
          result.completeResumeId !== input.completeResumeId ||
          result.candidateId !== input.candidateId ||
          result.decision !== "applied" ||
          typeof result.replayed !== "boolean"
        ) {
          throw new Error("Pickup result could not be confirmed.");
        }
        pickupAttemptRef.current = null;
        await invalidateOptimizationCaches();
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: orpc.multiResumeWorkspace.listPickupCandidates.queryKey({
              input: { completeResumeId: data.pickupReview.completeResumeId },
            }),
            refetchType: "all",
          }),
          queryClient.invalidateQueries({ queryKey: orpc.multiResumeWorkspace.state.queryKey(), refetchType: "all" }),
          queryClient.invalidateQueries({
            queryKey: orpc.resume.getById.queryKey({ input: { id: data.pickupReview.completeResumeId } }),
            refetchType: "all",
          }),
          queryClient.invalidateQueries({
            queryKey: orpc.resume.getById.queryKey({ input: { id: data.pickupReview.sourceTargetResumeId } }),
            refetchType: "all",
          }),
        ]);
        toast.success(t`The pickup was added to the Complete Resume.`);
        closeDialog();
      } catch {
        toast.error(t`The pickup could not be confirmed. Refresh pickup review and try again.`);
      } finally {
        pickupActionLockRef.current = false;
      }
      return;
    }

    const recommendation = activeRecommendation;
    const sectionId = snapshot.target.type === "project" ? "projects" : snapshot.target.type;
    if (recommendation?.planId && hasReviewableRecommendation(recommendation)) {
      if (!(await flushResumeSync({ refetch: false }))) {
        toast.error(t`Could not save the applied edit. The AI suggestion is still marked as unprocessed.`);
        return;
      }
      const acceptedData = structuredClone(resumeData);
      if (!applyAiEditDraftToResumeData(acceptedData, snapshot.target, draftToApply)) {
        toast.error(t`The AI edit target could not be found.`);
        return;
      }
      const priorAttempt = guidedApplyAttemptRef.current;
      const operationId =
        priorAttempt && isDeepEqual(priorAttempt.data, acceptedData) ? priorAttempt.operationId : generateId();
      guidedApplyAttemptRef.current = { operationId, data: structuredClone(acceptedData) };
      try {
        const response = await applyGuidedReviewProposal({
          resumeId,
          planId: recommendation.planId,
          sectionId,
          itemId: recommendation.itemId,
          operationId,
          data: acceptedData,
        });
        if (response.outcome === "conflict") {
          toast.error(
            response.reason === "workflow_unavailable"
              ? t`The saved review information for this suggestion is unavailable. Your edits have been kept.`
              : t`The resume or suggestion has changed. Your edits have been kept. Refresh the review before applying them.`,
          );
          return;
        }
        replaceResumeDataFromServer(acceptedData);
        guidedApplyAttemptRef.current = null;
        await invalidateOptimizationCaches();
      } catch (error) {
        console.warn("Failed to apply Guided Review proposal.", error);
        toast.error(t`The suggestion could not be confirmed. Please retry.`);
        return;
      }
    } else {
      let applied = false;
      updateResumeData((resumeDraft) => {
        applied = applyAiEditDraftToResumeData(resumeDraft, snapshot.target, draftToApply);
      });
      if (!applied) {
        toast.error(t`The AI edit target could not be found.`);
        return;
      }
      if (!(await flushResumeSync({ refetch: false }))) {
        toast.error(t`Could not save the applied edit.`);
        return;
      }
    }

    toast.success(t`AI optimized content applied.`);
    closeDialog();
  };

  const acceptHiddenRecommendation = async () => {
    if (!isHideInTargetRecommendation(activeRecommendation) || draft.type === "summary") {
      await applyResult();
      return;
    }

    await applyResult({ ...draft, hidden: true });
  };

  const ignoreRecommendation = async ({
    closeAfterIgnore = true,
    disposition = "generic_ignore",
  }: { closeAfterIgnore?: boolean; disposition?: "generic_ignore" | "inaccurate_feedback" } = {}) => {
    const recommendation = activeRecommendation;
    if (!snapshot || !recommendation || !hasReviewableRecommendation(recommendation)) return false;

    const sectionId = snapshot.target.type === "project" ? "projects" : snapshot.target.type;

    try {
      const response = await markOptimizationPlanItemIgnored({
        resumeId,
        planId: recommendation.planId,
        sectionId,
        itemId: recommendation.itemId,
        disposition,
      });
      const persistResult = resolveOptimizationPlanMutation(
        response,
        "ignored",
        recommendation,
        sectionId,
        disposition,
      );
      await invalidateOptimizationCaches();
      if (persistResult === "proposal_conflict") {
        toast.error(t`The AI suggestion status needs recovery. Refresh and try again.`);
        return false;
      }
      setDetachedRecommendationKey(recommendationKey);
      toast.success(
        disposition === "inaccurate_feedback" ? t`Inaccurate evidence was recorded.` : t`AI suggestion ignored.`,
      );
      if (closeAfterIgnore) closeDialog();
      else openManualEdit();
      return true;
    } catch (error) {
      console.warn("Failed to mark optimization plan item as ignored.", error);
      toast.error(t`Could not ignore this AI suggestion. Please try again.`);
      return false;
    }
  };

  const ignoreAndEditManually = async () => {
    await ignoreRecommendation({ closeAfterIgnore: snapshot?.target.type === "summary" });
  };

  const reportInaccurateEvidence = async () => {
    const confirmed = await confirm(t`Mark evidence as inaccurate?`, {
      description: t`This invalidates this recommendation. It will not change your resume.`,
      confirmText: t`Mark inaccurate`,
      cancelText: t`Keep recommendation`,
    });
    if (!confirmed) return;
    await ignoreRecommendation({ disposition: "inaccurate_feedback" });
  };

  if (!snapshot) {
    return (
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Trans>AI edit target not found</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>The section item may have been deleted. Close this dialog and try again.</Trans>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button type="button" onClick={() => closeDialog()}>
            <Trans>Close</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  const frozenOriginal = comparisonDraft ?? snapshot.original;

  return (
    <DialogContent
      className="flex h-[min(56rem,calc(100svh-2rem))] max-h-[calc(100svh-2rem)] w-[min(82.5rem,calc(100vw-2rem))] max-w-none! flex-col gap-4 overflow-hidden p-4 sm:max-w-none! sm:p-6 2xl:max-w-none!"
      data-testid="ai-edit-dialog"
      data-phase6-mode={isPickupReview ? "pickup" : "target"}
    >
      <DialogHeader className="shrink-0">
        <DialogTitle className="flex items-center gap-x-2">
          <PencilSimpleLineIcon />
          {getDialogTitle(snapshot)}
        </DialogTitle>
        <DialogDescription>
          <Trans>Edit the content directly, or open the AI panel for an assisted draft.</Trans>
        </DialogDescription>
      </DialogHeader>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {isAiExpanded ? (
          <AiExpandedComparisonPane
            mode={aiMode}
            originalDraft={frozenOriginal}
            draft={draft}
            response={response}
            customPrompt={customPrompt}
            recommendation={activeRecommendation}
            hasGeneratedResult={hasGeneratedResult}
            isPending={isPending}
            disableAiInputs={isHiddenInTarget}
            canUndo={draftHistory.past.length > 0}
            canRedo={draftHistory.future.length > 0}
            onChangeCustomPrompt={setCustomPrompt}
            onChangeDraft={commitDraft}
            onChangeHidden={commitDraft}
            onGenerate={() => void generateAiResult()}
            onUndo={undoDraft}
            onRedo={redoDraft}
            quotaCost={quotaCost}
          />
        ) : (
          <DefaultEditPane
            snapshot={snapshot}
            draft={draft}
            recommendation={activeRecommendation}
            onChangeDraft={commitDraft}
            onViewRecommendation={openRecommendationReview}
            onOneClickOptimize={openOneClickOptimize}
            onTuneDirection={openTuneDirection}
            onAcceptHiddenRecommendation={() => void acceptHiddenRecommendation()}
            onIgnoreRecommendation={() => void ignoreRecommendation()}
            isProcessingRecommendation={isPending || isIgnoringRecommendation || isApplyingPickup}
            quotaCost={quotaCost}
            isReviewingRecommendation={isReviewingRecommendation}
            onRegenerate={openOneClickOptimize}
          />
        )}
      </div>

      <DialogFooter className="shrink-0">
        <Button
          type="button"
          variant="ghost"
          onClick={() => void requestPickupClose()}
          disabled={pickupMutationPending}
        >
          <Trans>Cancel</Trans>
        </Button>

        {isReviewingRecommendation && !isPickupReview ? (
          <>
            <Button
              type="button"
              variant="outline"
              disabled={isPending || isIgnoringRecommendation}
              onClick={() => void reportInaccurateEvidence()}
            >
              <Trans>Evidence is inaccurate</Trans>
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isPending || isIgnoringRecommendation}
              onClick={() => void ignoreAndEditManually()}
            >
              {isIgnoringRecommendation ? <Trans>Ignoring...</Trans> : <Trans>Ignore and edit manually</Trans>}
            </Button>
          </>
        ) : isAiExpanded ? (
          <Button
            type="button"
            variant="outline"
            disabled={isPending || isIgnoringRecommendation}
            onClick={openManualEdit}
          >
            <Trans>Return to manual edit</Trans>
          </Button>
        ) : null}

        <Button
          type="button"
          disabled={isPending || isIgnoringRecommendation || isApplyingPickup || !canApply}
          onClick={() => void applyResult()}
        >
          <Trans>Apply to resume</Trans>
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
